import { useState } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { useAutoReformEnabled } from 'hooks/useAutoReformEnabled';

import {
  Text,
  Box,
  Flex,
  Button,
  useColorModeValue,
  Collapse,
  Icon,
} from '@chakra-ui/react';

import { DeleteIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';

import FormInput from 'components/fields/auto/FormInput';
import Divider from 'views/auto_form/components/Divider';

import {
  changeInsuranceCoverageLiability,
  changeInsuranceCovergaeAccidentBenefits,
  changeInsuranceCoverageUninsuredAutomobile,
  changeInsuranceCoverageDirectCompensation,
  changeInsuranceCoverageLossOrDamage,
  changePolicyChangeFormsFamilyProtectionCoverage,
  changePolicyChangeFormsOtherCoverages,
  addPolicyChangeFormsOtherCoverages,
  removePolicyChangeFormsOtherCoverages,
  changeInsuranceCoverageIncomeReplacement,
  changeInsuranceCoverageMedicalRehabilitation,
  changeInsuranceCoverageOptionalIncreasedAccidentBenefits,
  changeAbReformOptionalIncreasedAccidentBenefits,
} from 'reducers/autoPersonalFormReducer';

import useIsBinderDisabled from 'views/auto_form/hooks/useIsBinderDisabled';

import { SECTIONS } from 'views/auto_form/constants';
import { isNonOntarioProvince } from 'variables/constants';

const InsuranceCoverages = () => {
  const dispatch = useAppDispatch();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isAutoReformEnabled = useAutoReformEnabled();
  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const isNonOntario = isNonOntarioProvince(province);

  const insurance_coverages = useAppSelector(
    (state) => state.autoPersonalForm.data.insurance_coverages,
  );

  const isDisabled = useIsBinderDisabled(true);

  const sectionBg = useColorModeValue('#ccc', 'gray.600');
  const sectionColor = useColorModeValue('black', 'white');

  const onInsuranceCoverageLiabilityChange = (
    event: any,
    index: number,
    type: string,
  ) => {
    const { name, value } = event.target;
    const limit = type === 'limit' ? value : undefined;
    const premium = type === 'premium' ? value : undefined;

    dispatch(changeInsuranceCoverageLiability({ index, name, limit, premium }));
  };

  const onInsuranceCovergaeAccidentBenefitsChange = (
    event: any,
    index: number,
  ) => {
    dispatch(
      changeInsuranceCovergaeAccidentBenefits({
        index,
        value: event.target.value,
      }),
    );
  };

  const onInsuranceCoverageUninsuredAutomobileChange = (
    event: any,
    index: number,
  ) => {
    dispatch(
      changeInsuranceCoverageUninsuredAutomobile({
        index,
        value: event.target.value,
      }),
    );
  };

  const onInsuranceCoverageDirectCompensationChange = (
    event: any,
    index: number,
    type: string,
  ) => {
    const { value } = event.target;
    const deductible = type === 'deductible' ? value : undefined;
    const premium = type === 'premium' ? value : undefined;

    dispatch(
      changeInsuranceCoverageDirectCompensation({ index, deductible, premium }),
    );
  };

  const onInsuranceCoverageLossOrDamageChange = (
    event: any,
    index: number,
    type: string,
  ) => {
    const { value, name } = event.target;
    const deductible = type === 'deductible' ? value : undefined;
    const premium = type === 'premium' ? value : undefined;

    dispatch(
      changeInsuranceCoverageLossOrDamage({ index, name, deductible, premium }),
    );
  };

  const onPolicyChangeFormsFamilyProtectionCoverageChange = (
    event: any,
    index: number,
  ) => {
    dispatch(
      changePolicyChangeFormsFamilyProtectionCoverage({
        index,
        value: event.target.value,
      }),
    );
  };

  const onPolicyChangeFormsOtherCoveragesChange = (
    event: any,
    index: number,
    subIndex: number,
  ) => {
    const { name, value } = event.target;

    dispatch(
      changePolicyChangeFormsOtherCoverages({
        index,
        subIndex,
        name,
        value,
      }),
    );
  };

  const onInsuranceCoverageIncomeReplacementChange = (
    event: any,
    index: number,
    type: string,
  ) => {
    const { value } = event.target;
    const limit = type === 'limit' ? value : undefined;
    const premium = type === 'premium' ? value : undefined;

    dispatch(
      changeInsuranceCoverageIncomeReplacement({ index, limit, premium }),
    );
  };

  const onInsuranceCoverageMedicalRehabilitationChange = (
    event: any,
    index: number,
    type: string,
  ) => {
    const { value } = event.target;
    const limit = type === 'limit' ? value : undefined;
    const premium = type === 'premium' ? value : undefined;

    dispatch(
      changeInsuranceCoverageMedicalRehabilitation({ index, limit, premium }),
    );
  };

  const onInsuranceCoverageOptionalIncreasedAccidentBenefitsChange = (
    event: any,
    index: number,
  ) => {
    const { value, name } = event.target;

    dispatch(
      changeInsuranceCoverageOptionalIncreasedAccidentBenefits({
        index,
        name,
        value,
      }),
    );
  };

  const onAbReformOptionalIncreasedAccidentBenefitsChange = (
    event: any,
    index: number,
    type: 'limit' | 'premium',
  ) => {
    const { value, name } = event.target;
    const limit = type === 'limit' ? value : undefined;
    const premium = type === 'premium' ? value : undefined;

    dispatch(
      changeAbReformOptionalIncreasedAccidentBenefits({
        index,
        name,
        limit,
        premium,
      }),
    );
  };

  return (
    <Card mb="20px" id="sectionInsuranceCoverages">
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontWeight="bold" fontSize="xl">
          Insurance Coverage Summary{' '}
          <Text textColor="red" display="inline">
            (dev mode)
          </Text>
        </Text>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
          rightIcon={
            <Icon as={isCollapsed ? ChevronDownIcon : ChevronUpIcon} />
          }
        >
          {isCollapsed ? 'Expand' : 'Collapse'}
        </Button>
      </Flex>

      <Collapse in={!isCollapsed} animateOpacity>
        {insurance_coverages.map((insurance_coverage, index) => {
          return (
            <Box key={index} mt="20px">
              <Divider mb="20px">Automobile {index + 1}</Divider>

              <Flex gap="20px">
                <Box w="50%">
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                  >
                    Liability
                  </Box>

                  <Flex gap="20px" mt="20px">
                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="BI & PD (limit 000s)"
                      name="bodily_injury"
                      value={insurance_coverage.liability.bodily_injury.limit}
                      onChange={(e) =>
                        onInsuranceCoverageLiabilityChange(e, index, 'limit')
                      }
                      disabled
                    />
                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Bodily Injury (premium)"
                      name="bodily_injury"
                      value={insurance_coverage.liability.bodily_injury.premium}
                      onChange={(e) =>
                        onInsuranceCoverageLiabilityChange(e, index, 'premium')
                      }
                      disabled
                    />

                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Property Damage (premium)"
                      name="property_damage"
                      value={
                        insurance_coverage.liability.property_damage.premium
                      }
                      onChange={(e) =>
                        onInsuranceCoverageLiabilityChange(e, index, 'premium')
                      }
                      disabled
                    />
                  </Flex>
                </Box>
                <Box w="25%">
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                  >
                    Accident Benefits
                  </Box>

                  <Flex gap="20px" mt="20px">
                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Accident Benefits (premium)"
                      name="accident_benefits"
                      value={insurance_coverage.accident_benefits.premium}
                      onChange={(e) =>
                        onInsuranceCovergaeAccidentBenefitsChange(e, index)
                      }
                      disabled
                    />
                  </Flex>
                </Box>
                <Box w="25%">
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                  >
                    Uninsured Automobile
                  </Box>

                  <Flex gap="20px" mt="20px">
                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Uninsured Automobile (premium)"
                      name="uninsured_automobile"
                      value={insurance_coverage.uninsured_automobile.premium}
                      onChange={(e) =>
                        onInsuranceCoverageUninsuredAutomobileChange(e, index)
                      }
                      disabled
                    />
                  </Flex>
                </Box>
              </Flex>

              {!isNonOntario && (
                <Box
                  backgroundColor={sectionBg}
                  color={sectionColor}
                  rounded="5px"
                  p="5px"
                  pl="10px"
                  mt="20px"
                >
                  {isAutoReformEnabled
                    ? 'Optional & Increased Accident Benefits'
                    : 'Optional Increased Accident Benefits'}
                </Box>
              )}

              {!isNonOntario &&
                (isAutoReformEnabled ? (
                  <>
                    <Flex gap="20px" mt="20px" pl="20px">
                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Income Replacement
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Income Replacement (limit)"
                            name="income_replacement"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .income_replacement.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Income Replacement (premium)"
                            name="income_replacement"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .income_replacement.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>

                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Medical Rehabilitation
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Medical Rehabilitation (limit)"
                            name="medical_rehabilitation"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .medical_rehabilitation.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Medical Rehabilitation (premium)"
                            name="medical_rehabilitation"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .medical_rehabilitation.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>
                    </Flex>

                    <Flex gap="20px" mt="20px" pl="20px">
                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Optional Catastrophic Impairment
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Optional Catastrophic Impairment (limit)"
                            name="optional_catastrophic_impairment"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .optional_catastrophic_impairment.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Optional Catastrophic Impairment (premium)"
                            name="optional_catastrophic_impairment"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .optional_catastrophic_impairment.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>

                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Non-Earner
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Non-Earner (limit)"
                            name="non_earner"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .non_earner.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Non-Earner (premium)"
                            name="non_earner"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .non_earner.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>
                    </Flex>

                    <Flex gap="20px" mt="20px" pl="20px">
                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Caregiver
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Caregiver (limit)"
                            name="caregiver"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .caregiver.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Caregiver (premium)"
                            name="caregiver"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .caregiver.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>

                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Lost Educational Expenses
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Lost Educational Expenses (limit)"
                            name="lost_educational_expenses"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .lost_educational_expenses.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Lost Educational Expenses (premium)"
                            name="lost_educational_expenses"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .lost_educational_expenses.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>
                    </Flex>

                    <Flex gap="20px" mt="20px" pl="20px">
                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Expenses of Visitors
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Expenses of Visitors (limit)"
                            name="expenses_of_visitors"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .expenses_of_visitors.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Expenses of Visitors (premium)"
                            name="expenses_of_visitors"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .expenses_of_visitors.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>

                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Housekeeping & Home Maintenance
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Housekeeping & Home Maintenance (limit)"
                            name="housekeeping_home_maintenance"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .housekeeping_home_maintenance.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Housekeeping & Home Maintenance (premium)"
                            name="housekeeping_home_maintenance"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .housekeeping_home_maintenance.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>
                    </Flex>

                    <Flex gap="20px" mt="20px" pl="20px">
                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Damage to Personal Items
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Damage to Personal Items (limit)"
                            name="damage_to_personal_items"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .damage_to_personal_items.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Damage to Personal Items (premium)"
                            name="damage_to_personal_items"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .damage_to_personal_items.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>

                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Death
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Death (limit)"
                            name="death"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .death.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Death (premium)"
                            name="death"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .death.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>
                    </Flex>

                    <Flex gap="20px" mt="20px" pl="20px">
                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Funeral
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Funeral (limit)"
                            name="funeral"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .funeral.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Funeral (premium)"
                            name="funeral"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .funeral.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>

                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Dependant Care
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Dependant Care (limit)"
                            name="dependant_care"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .dependant_care.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Dependant Care (premium)"
                            name="dependant_care"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .dependant_care.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>
                    </Flex>

                    <Flex gap="20px" mt="20px" pl="20px">
                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Indexation Benefit
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Indexation Benefit (limit)"
                            name="indexation_benefit"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .indexation_benefit.limit
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Indexation Benefit (premium)"
                            name="indexation_benefit"
                            value={
                              insurance_coverage
                                .ab_reform_optional_increased_accident_benefits
                                .indexation_benefit.premium
                            }
                            onChange={(e) =>
                              onAbReformOptionalIncreasedAccidentBenefitsChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>

                      <Box w="50%" />
                    </Flex>
                  </>
                ) : (
                  <>
                    <Flex gap="20px" mt="20px" pl="20px">
                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Income Replacement ($600/$800/$1,000)
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Income Replacement (limit)"
                            name="income_replacement"
                            value={
                              insurance_coverage
                                .optional_increased_accident_benefits
                                .income_replacement.limit
                            }
                            onChange={(e) =>
                              onInsuranceCoverageIncomeReplacementChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Income Replacement (premium)"
                            name="income_replacement"
                            value={
                              insurance_coverage
                                .optional_increased_accident_benefits
                                .income_replacement.premium
                            }
                            onChange={(e) =>
                              onInsuranceCoverageIncomeReplacementChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>

                      <Box w="50%">
                        <Box
                          backgroundColor={sectionBg}
                          color={sectionColor}
                          rounded="5px"
                          p="5px"
                          pl="10px"
                        >
                          Medical, Rehabilitation & Attendant Care
                          ($130,000/$1,000,000)
                        </Box>

                        <Flex gap="20px" mt="20px">
                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Medical, Rehabilitation & Attendant Care (limit)"
                            name="medical_rehabilitation"
                            value={
                              insurance_coverage
                                .optional_increased_accident_benefits
                                .medical_rehabilitation.limit
                            }
                            onChange={(e) =>
                              onInsuranceCoverageMedicalRehabilitationChange(
                                e,
                                index,
                                'limit',
                              )
                            }
                            disabled
                          />

                          <FormInput
                            section={SECTIONS.INSURANCE_COVERAGES}
                            label="Medical, Rehabilitation & Attendant Care (premium)"
                            name="medical_rehabilitation"
                            value={
                              insurance_coverage
                                .optional_increased_accident_benefits
                                .medical_rehabilitation.premium
                            }
                            onChange={(e) =>
                              onInsuranceCoverageMedicalRehabilitationChange(
                                e,
                                index,
                                'premium',
                              )
                            }
                            disabled
                          />
                        </Flex>
                      </Box>
                    </Flex>

                    <Box
                      backgroundColor={sectionBg}
                      color={sectionColor}
                      rounded="5px"
                      p="5px"
                      pl="10px"
                      w="100%"
                      ml="20px"
                      mt="20px"
                    >
                      Other
                    </Box>

                    <Flex gap="20px" mt="20px" pl="20px">
                      <FormInput
                        section={SECTIONS.INSURANCE_COVERAGES}
                        label="Optional Catastrophic Impairment (premium)"
                        name="optional_catastrophic_impairment"
                        value={
                          insurance_coverage
                            .optional_increased_accident_benefits
                            .optional_catastrophic_impairment
                        }
                        onChange={(e) =>
                          onInsuranceCoverageOptionalIncreasedAccidentBenefitsChange(
                            e,
                            index,
                          )
                        }
                        infoText="(additional $1,000,000 added to Standard Benefit or Optional Medical, Rehabilitation & Attendant Care Benefit)"
                        disabled
                      />

                      <FormInput
                        section={SECTIONS.INSURANCE_COVERAGES}
                        label="Caregiver, Housekeeping & Home Maintenance (premium)"
                        name="caregiver_housekeeping_home_maintenance"
                        value={
                          insurance_coverage
                            .optional_increased_accident_benefits
                            .caregiver_housekeeping_home_maintenance
                        }
                        onChange={(e) =>
                          onInsuranceCoverageOptionalIncreasedAccidentBenefitsChange(
                            e,
                            index,
                          )
                        }
                        disabled
                      />

                      <FormInput
                        section={SECTIONS.INSURANCE_COVERAGES}
                        label="Death & Funeral (premium)"
                        name="death_funeral"
                        value={
                          insurance_coverage
                            .optional_increased_accident_benefits.death_funeral
                        }
                        onChange={(e) =>
                          onInsuranceCoverageOptionalIncreasedAccidentBenefitsChange(
                            e,
                            index,
                          )
                        }
                        disabled
                      />
                    </Flex>

                    <Flex gap="20px" mt="20px" pl="20px">
                      <FormInput
                        section={SECTIONS.INSURANCE_COVERAGES}
                        label="Dependant Care  (premium)"
                        name="dependant_care"
                        value={
                          insurance_coverage
                            .optional_increased_accident_benefits.dependant_care
                        }
                        onChange={(e) =>
                          onInsuranceCoverageOptionalIncreasedAccidentBenefitsChange(
                            e,
                            index,
                          )
                        }
                        disabled
                      />

                      <FormInput
                        section={SECTIONS.INSURANCE_COVERAGES}
                        label="Indexation Benefit (Consumer Price Index) (premium)"
                        name="indexation_benefit"
                        value={
                          insurance_coverage
                            .optional_increased_accident_benefits
                            .indexation_benefit
                        }
                        onChange={(e) =>
                          onInsuranceCoverageOptionalIncreasedAccidentBenefitsChange(
                            e,
                            index,
                          )
                        }
                        disabled
                      />
                    </Flex>
                  </>
                ))}

              <Box
                backgroundColor={sectionBg}
                color={sectionColor}
                rounded="5px"
                p="5px"
                pl="10px"
                mt="20px"
              >
                Direct Compensation
              </Box>

              <Flex gap="20px" mt="20px">
                <FormInput
                  section={SECTIONS.INSURANCE_COVERAGES}
                  label="Direct Compensation (deductible)"
                  name="direct_compensation"
                  value={insurance_coverage.direct_compensation.deductible}
                  onChange={(e) =>
                    onInsuranceCoverageDirectCompensationChange(
                      e,
                      index,
                      'deductible',
                    )
                  }
                  disabled
                />

                <FormInput
                  section={SECTIONS.INSURANCE_COVERAGES}
                  label="Direct Compensation (premium)"
                  name="direct_compensation"
                  value={insurance_coverage.direct_compensation.premium}
                  onChange={(e) =>
                    onInsuranceCoverageDirectCompensationChange(
                      e,
                      index,
                      'premium',
                    )
                  }
                  disabled
                />
              </Flex>

              <Box
                backgroundColor={sectionBg}
                color={sectionColor}
                rounded="5px"
                p="5px"
                pl="10px"
                mt="20px"
              >
                Loss or Damage
              </Box>

              <Flex gap="20px" mt="20px" pl="20px">
                <Box w="50%">
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                  >
                    Specified Perils (excluding Collision or Upset)
                  </Box>

                  <Flex gap="20px" mt="20px">
                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Specified Perils (deductible)"
                      name="specified_perils"
                      value={
                        insurance_coverage.loss_or_damage.specified_perils
                          .deductible
                      }
                      onChange={(e) =>
                        onInsuranceCoverageLossOrDamageChange(
                          e,
                          index,
                          'deductible',
                        )
                      }
                      disabled
                    />

                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Specified Perils (premium)"
                      name="specified_perils"
                      value={
                        insurance_coverage.loss_or_damage.specified_perils
                          .premium
                      }
                      onChange={(e) =>
                        onInsuranceCoverageLossOrDamageChange(
                          e,
                          index,
                          'premium',
                        )
                      }
                      disabled
                    />
                  </Flex>
                </Box>
                <Box w="50%">
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                  >
                    Comprehensive (excluding Collision or Upset)
                  </Box>

                  <Flex gap="20px" mt="20px">
                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Comprehensive (deductible)"
                      name="comprehensive"
                      value={
                        insurance_coverage.loss_or_damage.comprehensive
                          .deductible
                      }
                      onChange={(e) =>
                        onInsuranceCoverageLossOrDamageChange(
                          e,
                          index,
                          'deductible',
                        )
                      }
                      disabled
                    />

                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Comprehensive (premium)"
                      name="comprehensive"
                      value={
                        insurance_coverage.loss_or_damage.comprehensive.premium
                      }
                      onChange={(e) =>
                        onInsuranceCoverageLossOrDamageChange(
                          e,
                          index,
                          'premium',
                        )
                      }
                      disabled
                    />
                  </Flex>
                </Box>
              </Flex>

              <Flex gap="20px" mt="20px" pl="20px">
                <Box w="50%">
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                  >
                    Collision or Upset
                  </Box>

                  <Flex gap="20px" mt="20px">
                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Collision or Upset (deductible)"
                      name="collision_or_upset"
                      value={
                        insurance_coverage.loss_or_damage.collision_or_upset
                          .deductible
                      }
                      onChange={(e) =>
                        onInsuranceCoverageLossOrDamageChange(
                          e,
                          index,
                          'deductible',
                        )
                      }
                      disabled
                    />

                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="Collision or Upset (premium)"
                      name="collision_or_upset"
                      value={
                        insurance_coverage.loss_or_damage.collision_or_upset
                          .premium
                      }
                      onChange={(e) =>
                        onInsuranceCoverageLossOrDamageChange(
                          e,
                          index,
                          'premium',
                        )
                      }
                      disabled
                    />
                  </Flex>
                </Box>
                <Box w="50%">
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                  >
                    All Perils
                  </Box>

                  <Flex gap="20px" mt="20px">
                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="All Perils (deductible)"
                      name="all_perils"
                      value={
                        insurance_coverage.loss_or_damage.all_perils.deductible
                      }
                      onChange={(e) =>
                        onInsuranceCoverageLossOrDamageChange(
                          e,
                          index,
                          'deductible',
                        )
                      }
                      disabled
                    />

                    <FormInput
                      section={SECTIONS.INSURANCE_COVERAGES}
                      label="All Perils (premium)"
                      name="all_perils"
                      value={
                        insurance_coverage.loss_or_damage.all_perils.premium
                      }
                      onChange={(e) =>
                        onInsuranceCoverageLossOrDamageChange(
                          e,
                          index,
                          'premium',
                        )
                      }
                      disabled
                    />
                  </Flex>
                </Box>
              </Flex>

              <Box
                backgroundColor={sectionBg}
                color={sectionColor}
                rounded="5px"
                p="5px"
                pl="10px"
                mt="20px"
                mb="20px"
              >
                Policy Change Forms
              </Box>

              <FormInput
                section={SECTIONS.INSURANCE_COVERAGES}
                label="Family Protection Coverage - OPCF 44R (premium)"
                name="family_protection_coverage"
                value={
                  insurance_coverage.policy_change_forms
                    .family_protection_coverage.premium
                }
                onChange={(e) =>
                  onPolicyChangeFormsFamilyProtectionCoverageChange(e, index)
                }
                disabled
              />

              {insurance_coverage.policy_change_forms.other_coverages.map(
                (other_coverage, otherCoverageIndex) => {
                  return (
                    <Flex gap="20px" mt="20px" key={otherCoverageIndex}>
                      <FormInput
                        section={SECTIONS.INSURANCE_COVERAGES}
                        label="Name"
                        name="name"
                        value={other_coverage.name}
                        onChange={(e) =>
                          onPolicyChangeFormsOtherCoveragesChange(
                            e,
                            index,
                            otherCoverageIndex,
                          )
                        }
                        disabled
                      />

                      <FormInput
                        section={SECTIONS.INSURANCE_COVERAGES}
                        label="Deductible/limit"
                        name="deductible_limit"
                        value={other_coverage.deductible_limit}
                        onChange={(e) =>
                          onPolicyChangeFormsOtherCoveragesChange(
                            e,
                            index,
                            otherCoverageIndex,
                          )
                        }
                        disabled
                      />

                      <FormInput
                        section={SECTIONS.INSURANCE_COVERAGES}
                        label="Premium"
                        name="premium"
                        value={other_coverage.premium}
                        onChange={(e) =>
                          onPolicyChangeFormsOtherCoveragesChange(
                            e,
                            index,
                            otherCoverageIndex,
                          )
                        }
                        disabled
                      />

                      {!isDisabled && (
                        <Button
                          mt="30px"
                          onClick={(e) =>
                            dispatch(
                              removePolicyChangeFormsOtherCoverages({
                                index,
                                subIndex: otherCoverageIndex,
                              }),
                            )
                          }
                        >
                          <DeleteIcon />
                        </Button>
                      )}
                    </Flex>
                  );
                },
              )}

              {!isDisabled && (
                <Box mt="20px">
                  <Button
                    variant="brand"
                    onClick={() =>
                      dispatch(addPolicyChangeFormsOtherCoverages(index))
                    }
                  >
                    Add coverage
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Collapse>
    </Card>
  );
};

export default InsuranceCoverages;
